import type { CruisesContract } from '@luxuryescapes/contract-svc-cruise'
import type { Order as OrderTypes } from '@luxuryescapes/contract-svc-order'
import { OFFER_TYPE_CRUISE } from 'constants/offer'
import { filterDataByHighestPercentage } from 'lib/cruises/cruiseUtils'
import { convertTZ } from 'lib/datetime/dateUtils'
import { mapCruiseBookingBundles, mapCruiseOfferBundle } from './cruiseBundleMap'
import { cruiseInclusionsMap, cruiseBookingInclusionsMap, cruiseEvergreenInclusionsMap } from './cruiseInclusionMap'
import { CRUISE_LINE_CUSTOM_INFO, CRUISE_LINE_INFO, DEFAULT_CRUISE_LINE_DEALS_PAGE_INFO } from 'constants/cruise'
import type { CruiseAPI } from 'api/cruises'
import moment from 'moment'
import config from 'constants/config'
import { arrayToObject } from 'lib/array/arrayUtils'
import { capitalise, capitaliseIgnoreSet } from 'lib/string/stringUtils'

type CruiseOfferMapInput = Omit<CruisesContract.Offer, 'dealPages'> & {
  dealPages?: CruisesContract.Offer['dealPages']
}

type CruiseProductTypeInput = {
  leExclusive: boolean
  luxPremiumCollection: boolean
}

export function formatOfferName(name: string, displayName?: string): string {
  // It must be formatted because the name came from the provider
  if (!displayName) return capitaliseAll(name.toLowerCase())
  // Must be formatted because displayName is the same as name
  return displayName
}

export function cruisesMap(cruises: Array<CruisesContract.OfferSummary>): Array<App.CruiseOfferSummary> {
  return cruises.map((cruise) => {
    const offerName = formatOfferName(cruise.name, cruise.displayName)
    const ship = cruisesMapShip(cruise.ship, offerName)
    const cruiseLineCustomInfo = CRUISE_LINE_CUSTOM_INFO.find((info) => info.code === cruise.cruiseLine.code)

    return {
      id: cruise.id,
      type: OFFER_TYPE_CRUISE,
      cruiseLine: {
        name: cruiseLineCustomInfo?.name ?? cruise.cruiseLine.name,
        imageId: cruise.cruiseLine.imageId,
        code: cruise.cruiseLine.code,
      },
      disableDeposits: !config.DEPOSITS_ENABLED || cruise.disableDeposits,
      duration: cruise.duration,
      leExclusive: cruise.leExclusive,
      heroImageId: cruise.heroImageId,
      name: offerName,
      departureDates: cruise.departureDates,
      departurePort: cruise.departurePort.name,
      returnPort: cruise.returnPort.name,
      ship,
      images: ship.images,
      lowestDeparturePrice: cruise.lowestDeparturePrice,
      description: cruise.leVisibleDescription,
      destinationDescription: cruise.destinationDescription,
      itinerary: cruiseItinerary(cruise.itineraries, cruise.cruiseTourItineraries),
      campaignTags: cruise.campaigns ?? [],
    }
  })
}

export function cruiseDealMap(cruiseDeal: CruiseAPI.CruiseDeal): App.Cruises.CruiseDeal {
  return {
    id: cruiseDeal.id,
    name: cruiseDeal.name,
    slug: cruiseDeal.slug,
    heading: cruiseDeal.heading,
    subHeading: cruiseDeal.subHeading,
    termsAndConditions: cruiseDeal.termsAndConditions,
    imageId: cruiseDeal.imageId ?? undefined,
    cruiseLine: {
      id: cruiseDeal.cruiseLine.id,
      name: cruiseDeal.cruiseLine.name,
      code: cruiseDeal.cruiseLine.code,
      whiteImageId: cruiseDeal.cruiseLine.whiteImageId ?? undefined,
    },
    startDate: cruiseDeal.startDate,
    endDate: cruiseDeal.endDate,
    rates: cruiseDeal.rates.map(rate => ({
      id: rate.id,
      code: rate.code,
    })),
    departures: cruiseDeal.departures?.map(departure => ({
      id: departure.id,
      name: departure.name,
      externalId: departure.externalId,
    })) ?? [],
    inclusions: cruiseDeal.inclusions?.map((inclusion) => ({
      id: inclusion.id,
      description: inclusion.description,
      icon: inclusion.icon as App.OfferFeatureSymbol,
      order: inclusion.order,
      isHighlighted: inclusion.isHighlighted,
    })) ?? [],
  }
}

export function cruiseDealsMap(cruiseDeals: Array<CruiseAPI.CruiseDealBase>): Array<App.Cruises.CruiseDealBase> {
  let defaultHeroImageId = DEFAULT_CRUISE_LINE_DEALS_PAGE_INFO.dealPageImageId
  let pageColour = DEFAULT_CRUISE_LINE_DEALS_PAGE_INFO.dealPageColour
  return cruiseDeals.map((cruiseDeal) => {
    if (cruiseDeal.cruiseLine.code in CRUISE_LINE_INFO) {
      const cruiseLineInfo = CRUISE_LINE_INFO[cruiseDeal.cruiseLine.code as keyof typeof CRUISE_LINE_INFO]
      defaultHeroImageId = cruiseLineInfo.dealPageImageId
      pageColour = cruiseLineInfo.dealPageColour
    }

    return {
      id: cruiseDeal.id,
      slug: cruiseDeal.slug,
      heading: cruiseDeal.heading,
      subHeading: cruiseDeal.subHeading,
      heroImageId: cruiseDeal.heroImageId || defaultHeroImageId,
      pageColour,
      cruiseLine: cruiseLineDealPageMap(cruiseDeal.cruiseLine),
      endDate: cruiseDeal.endDate,
    }
  })
}

function cruiseLineDealPageMap(cruiseLine: CruiseAPI.CruiseDealBase['cruiseLine']): App.Cruises.CruiseDealBase['cruiseLine'] {
  return {
    name: cruiseLine.name,
    code: cruiseLine.code,
    whiteImageId: cruiseLine.whiteImageId ?? undefined,
  }
}

export function cruiseLineMap(cruiseLine: CruisesContract.CruiseLine): App.CruiseLine {
  const customInfo = CRUISE_LINE_CUSTOM_INFO.find(({ code }) => code === cruiseLine.code)

  return {
    id: cruiseLine.id,
    name: customInfo?.name ?? cruiseLine.name,
    code: cruiseLine.code,
    imageId: cruiseLine.imageId,
    description: cruiseLine.description,
    lpHeroImageId: cruiseLine.lpHeroImageId,
    lpMobileHeroImageId: cruiseLine.lpMobileHeroImageId,
    lpEnabled: cruiseLine.lpEnabled,
    slug: cruiseLine.slug,
  }
}

export function getCruiseProductType(
  cruise: CruiseProductTypeInput,
): App.CruiseProductType {
  if (cruise.leExclusive) {
    return 'cruise-exclusive'
  }
  else if (cruise.luxPremiumCollection) {
    return 'cruise-tactical'
  }
  else {
    return 'cruise'
  }
}

type PriceDetailsParams = {
  currencyCode?: string
  isTaxIncluded?: boolean
  isNccfIncluded?: boolean
} & CruisesContract.LowestPriceWithCategory

export function buildPriceDetails(
  price: PriceDetailsParams,
): App.CruisePriceDetails {
  return {
    total: Math.round(price.total),
    value: price.value ?? Math.round(price.total * 2),
    adultFare: Math.round(price.adultFare),
    cabinCategory: price.cabinCategory,
    isTaxIncluded: !!price.isTaxIncluded,
    isNccfIncluded: !!price.isNccfIncluded,
    rateCode: price.rateCode ?? undefined,
    currencyCode: price.currencyCode ?? undefined,
    // WE JUST HAVE DISCOUNT PILLS FOR VALUES COMING FROM CACHE
    discountPills: price.brochurePrice && price.brochureDiscountPrice && price.brochureDiscountPercentage ? {
      price: Math.round(price.brochurePrice),
      discountPrice: Math.floor(price.brochureDiscountPrice),
      discountPercentage: price.brochureDiscountPercentage,
    } : undefined,
  }
}

export function buildLowestDeparturePricesByCategory(
  lowestDeparturePricesByCategory: CruisesContract.LowestDeparturePricesByCategory,
): App.LowestCabinDetailsByDepartureId {
  const lowestCabinDetailsByDeparture: App.LowestCabinDetailsByDepartureId = {}

  Object.entries(lowestDeparturePricesByCategory).forEach(([id, lowestPricesByCategory]) => {
    Object.entries(lowestPricesByCategory).forEach(([_, price]) => {
      const category = price.cabinCategory
      const priceDetails = buildPriceDetails(price)
      lowestCabinDetailsByDeparture[id] = {
        ...lowestCabinDetailsByDeparture[id],
        [category]: priceDetails,
      }
    })
  })

  return lowestCabinDetailsByDeparture
}

type LowestCabinsDetailsByDepartureWithId = {
  lowestDepartureId: string
  lowestPriceDetailsByCategory: App.LowestPriceDetailsByCategory
}

export function getLowestCabinsDetailsByDeparture(
  lowestCabinDetailsByDeparture: App.LowestCabinDetailsByDepartureId,
): LowestCabinsDetailsByDepartureWithId {
  let lowestDepartureId: string = ''
  const lowestPriceDetailsByCategory: App.LowestPriceDetailsByCategory = {}

  Object.entries(lowestCabinDetailsByDeparture).forEach(([id, lowestPricesByCategory]) => {
    Object.values(lowestPricesByCategory).forEach((price: App.CruisePriceDetails) => {
      const category = price.cabinCategory
      const lowestPrice = lowestPriceDetailsByCategory[category]

      if (!lowestPrice) {
        lowestDepartureId = id
        lowestPriceDetailsByCategory[category] = price
      }
      else if (price.total < lowestPrice.total) {
        lowestDepartureId = id
        lowestPriceDetailsByCategory[category] = price
      }
    })
  })

  return { lowestDepartureId, lowestPriceDetailsByCategory }
}

export function getLowestOverallPriceDetails(
  lowestPriceDetailsByCategory: App.LowestPriceDetailsByCategory,
): App.CruisePriceDetails {
  return Object.values(lowestPriceDetailsByCategory).reduce(
    (acc: App.CruisePriceDetails, cur: App.CruisePriceDetails) => {
      if (!acc) return cur
      return acc.total < cur.total ? acc : cur
    }, {} as App.CruisePriceDetails)
}

type CruiseDepartureDetailsParams = {
  duration: number
  lowestDeparturePricesByCategory: CruisesContract.LowestDeparturePricesByCategory
  mainDepartureId?: string
  departuresByMonth?: CruisesContract.DeparturesByMonth
  inclusions?: Array<App.Cruises.OfferInclusion>
}

export function buildMainDepartureDetails(params: CruiseDepartureDetailsParams, regionCode: string): App.CruiseDepartureDetails {
  const { mainDepartureId, lowestDeparturePricesByCategory, departuresByMonth } = params

  const lowestCabinDetailsByDeparture = buildLowestDeparturePricesByCategory(lowestDeparturePricesByCategory)

  let departureId: string | null = mainDepartureId ?? null
  let lowestPriceDetailsByCategory: App.LowestPriceDetailsByCategory = {}

  /** IF NOT DEPARTURE ID, USE THE
   * LOWEST PRICE FROM ALL DEPARTURES */
  if (!departureId || !lowestCabinDetailsByDeparture[departureId]) {
    const result = getLowestCabinsDetailsByDeparture(lowestCabinDetailsByDeparture)
    departureId = result.lowestDepartureId
    lowestPriceDetailsByCategory = result.lowestPriceDetailsByCategory
  } else lowestPriceDetailsByCategory = lowestCabinDetailsByDeparture[departureId]

  const lowestOverallPriceDetails = getLowestOverallPriceDetails(lowestPriceDetailsByCategory)

  const departureExtraInfo: (CruisesContract.Departure & { minimumSupervisorAge?: number; hasMemberPricing?: boolean }) | null = Object.entries(departuresByMonth || {})
    .flatMap(([_, departures]) => departures).find((departure) => departure.id === departureId) ?? null

  const { luxPlusInclusionsByTier, standardInclusions, inclusionDetails } = cruiseInclusionsMap({
    inclusions: params.inclusions ?? [],
    departureId,
    lowestRateCode: lowestOverallPriceDetails.rateCode!,
    regionCode,
    isMainDeparture: true,
  })

  return {
    id: departureId,
    lowestOverallPriceDetails,
    lowestPriceDetailsByCategory,
    departureDate: departureExtraInfo?.departureDate ?? null,
    externalId: departureExtraInfo?.externalId ?? null,
    arrivalDate: departureExtraInfo?.arrivalDate ?? null,
    minimumSupervisorAge: departureExtraInfo?.minimumSupervisorAge!,
    luxPlusInclusionsByTier,
    standardInclusions,
    inclusionDetails,
    hasMemberPrice: departureExtraInfo?.hasMemberPricing ?? false,
  }
}

export function convertDepartureToLowestDeparturePricesByCategory(
  departure: CruisesContract.Departure,
): CruisesContract.LowestDeparturePricesByCategory | null {
  return departure.prices?.reduce((acc, cur) => ({
    ...acc,
    [cur.cabinCategory]: {
      ...cur,
      total: cur.total,
      rateCode: cur.rateCode,
      adultFare: cur.adultFare,
      currencyCode: cur.currencyCode,
      cabinCategory: cur.cabinCategory,
      isTaxIncluded: cur.isTaxIncluded,
      isNccfIncluded: cur.isNccfIncluded,
      value: Math.round(cur.total * 2),
    },
  }), {}) ?? null
}

type LowestDepartureDetailsByIdParams = {
  duration: number
  departure: CruisesContract.Departure & { minimumSupervisorAge?: number; hasMemberPricing?: boolean }
  lowestDeparturePricesByCategory: CruisesContract.LowestDeparturePricesByCategory
  inclusions?: Array<App.Cruises.OfferInclusion>
}

export function buildLowestDepartureDetailsById(
  params: LowestDepartureDetailsByIdParams,
  regionCode: string,
): App.CruiseDepartureDetails {
  const { departure, lowestDeparturePricesByCategory } = params
  let lowestPriceDetailsByCategory: App.LowestPriceDetailsByCategory = {}

  if (!lowestDeparturePricesByCategory[departure.id]) {
    const departureByCategories = convertDepartureToLowestDeparturePricesByCategory(departure)
    const lowestCabinDetailsByDeparture = buildLowestDeparturePricesByCategory(departureByCategories ?? {})
    lowestPriceDetailsByCategory = getLowestCabinsDetailsByDeparture(lowestCabinDetailsByDeparture).lowestPriceDetailsByCategory
  } else {
    const lowestCabinDetailsByDeparture = buildLowestDeparturePricesByCategory(lowestDeparturePricesByCategory)
    lowestPriceDetailsByCategory = lowestCabinDetailsByDeparture[departure.id]
  }

  const lowestOverallPriceDetails = getLowestOverallPriceDetails(lowestPriceDetailsByCategory)

  const departureRates = departure.rateCodes ?? departure.prices?.map(price => price.rateCode) ?? []
  const { luxPlusInclusionsByTier, standardInclusions, inclusionDetails } = cruiseInclusionsMap({
    inclusions: params.inclusions ?? [],
    departureId: departure.id,
    lowestRateCode: lowestOverallPriceDetails.rateCode!,
    allRateCodes: departureRates,
    regionCode,
  })

  return {
    id: departure.id,
    lowestOverallPriceDetails,
    lowestPriceDetailsByCategory,
    departureDate: departure.departureDate ?? null,
    externalId: departure.externalId ?? null,
    arrivalDate: departure.arrivalDate ?? null,
    minimumSupervisorAge: departure.minimumSupervisorAge,
    luxPlusInclusionsByTier,
    standardInclusions,
    inclusionDetails,
    hasMemberPrice: departure.hasMemberPricing ?? false,
  }
}

export function getDepartureWithLowestPriceByRateCode(
  departuresByMonth: CruisesContract.DeparturesByMonth,
  targetRateCode: string,
): CruisesContract.Departure | null {
  const filteredDepartures = Object.values(departuresByMonth)
    .flatMap(month => month)
    .filter(departure => departure.prices?.some(price => price.rateCode === targetRateCode))

  if (filteredDepartures.length === 0) {
    return null
  }
  const lowestDeparture = filteredDepartures.reduce((minDeparture, departure) => {
    const lowestPriceInDeparture = (departure.prices || []).reduce(
      (minPrice, price) => (price.total < minPrice ? price.total : minPrice),
      Infinity,
    )

    if (lowestPriceInDeparture < minDeparture.price) {
      return { price: lowestPriceInDeparture, departure }
    }

    return minDeparture
  }, { price: Infinity, departure: null as CruisesContract.Departure | null })
  return lowestDeparture.departure
}

export function mapUrgencyTags() {
  // While this does nothing now, there are times we need to manaully add urgency tags to the
  // offer - such as the Best of the Decade 2023 campaign. Keep this method around to support
  // future extra mappings
  const tags: Array<string> = []
  return tags
}

export function cruiseOfferMap(cruise: CruiseOfferMapInput, regionCode: string): App.CruiseOffer {
  const offerName = formatOfferName(cruise.name, cruise.displayName)

  const ship = cruisesMapShip(cruise.ship, offerName)
  const { leadDepartureId, lowestDeparturePricesByCategory, departures } = cruise
  const offerInclusions = cruise.inclusions as Array<App.Cruises.OfferInclusion>

  const departuresByMonth = Object.keys(departures ?? {})
    .reduce((acc, cur) => ({
      ...acc,
      [cur]: departures?.[cur]?.map(
        (item) => {
          return buildLowestDepartureDetailsById({
            departure: item,
            duration: cruise.duration,
            lowestDeparturePricesByCategory,
            ...(offerInclusions && { inclusions: offerInclusions }),
          }, regionCode)
        }),
    }), {})

  const mainDepartureDetails: App.CruiseDepartureDetails = buildMainDepartureDetails({
    duration: cruise.duration,
    lowestDeparturePricesByCategory,
    ...(departures && { departuresByMonth: departures }),
    ...(leadDepartureId && { mainDepartureId: leadDepartureId }),
    ...(offerInclusions && { inclusions: offerInclusions }),
  }, regionCode)

  const cruiseLineInfo = CRUISE_LINE_CUSTOM_INFO.find((info) => info.code === cruise.cruiseLine.code)
  const hasMemberInclusions = !!mainDepartureDetails.luxPlusInclusionsByTier
  return {
    id: cruise.id,
    productType: getCruiseProductType(cruise),
    cruiseLine: {
      id: cruise.cruiseLine.id,
      name: cruiseLineInfo?.name ?? cruise.cruiseLine.name,
      code: cruise.cruiseLine.code,
      imageId: cruise.cruiseLine.imageId,
      vendorInfo: cruise.cruiseLine.vendorInfo?.map<App.CruiseVendorInfo>((serverVendorInfo) => {
        return {
          dataType: serverVendorInfo.dataType,
          vendorName: serverVendorInfo.vendorName ?? '',
          title: serverVendorInfo.title ?? '',
          text: serverVendorInfo.text ?? '',
          vendorId: serverVendorInfo.vendorId,
        }
      }),
    },

    duration: cruise.duration,
    disableDeposits: !config.DEPOSITS_ENABLED || cruise.disableDeposits,
    heroImageId: cruise.heroImageId,
    name: offerName,
    departureDates: cruise.departureDates,
    departurePort: cruise.departurePort.name,
    showPricePerNight: cruise.showPricePerNight,
    returnPort: cruise.returnPort.name,
    ship,
    slug: cruise.id,
    type: OFFER_TYPE_CRUISE,
    parentType: OFFER_TYPE_CRUISE,
    analyticsType: OFFER_TYPE_CRUISE,
    images: cruise?.images?.map(cruisesMapOfferImages) ?? [],
    faq: [].map(cruiseFaqMap), // TODO: Implement faq for cruises
    finePrint: cruiseFinePrintMap(), // TODO: Implement finePrint for cruises
    departures: departuresByMonth,
    itinerary: cruiseItinerary(cruise.itineraries, cruise.cruiseTourItineraries),
    destinationDescription: cruise.destinationDescription,
    description: cruise.leVisibleDescription,
    // these types are not correct from server, cannot work for now. Any'ing them
    lowestDeparturePricesByCategory: cruise.lowestDeparturePricesByCategory as any,
    lowestDeparturePrice: cruise.lowestDeparturePrice as any, /** @deprecated - use mainDepartureDetails.lowestOverallPriceDetails instead */
    mainDepartureDetails,
    bundle: mapCruiseOfferBundle(cruise as Parameters<typeof mapCruiseOfferBundle>[0]),
    evergreenInclusions: cruiseEvergreenInclusionsMap(cruise.inclusions as Array<CruiseAPI.OfferInclusion>),
    urgencyTags: mapUrgencyTags(),
    luxPlus: {
      hasMemberInclusions,
    },
    offerInclusions,
    dealPages: cruise.dealPages ?? [],
    loyaltyProductType: cruise.loyaltyProductType,
    earnKey: cruise.earnKey ?? undefined,
    campaignTags: cruise.campaigns ?? [],
    cruiseType: cruise.cruiseType,
  }
}

export function cruiseLowestPriceByMonthMap(
  prices: Array<CruisesContract.LowestPriceByMonth>,
  fromDate: string,
): Array<App.CruiseLowestPriceByMonth> {
  return prices.map((price): App.CruiseLowestPriceByMonth => ({
    year: moment(fromDate).year(),
    month: price.month,
    totalFare: Math.ceil(price.totalFare),
  }))
}

export function cruiseOrderItemMap(orderItem: any): App.OrderCruiseItem {
  return {
    sessionId: orderItem.session_id,
    bookingId: orderItem.booking_id,
    departureId: orderItem.departure_id,
    departureDate: orderItem.departure_date,
    arrivalDate: orderItem.arrival_date,
    id: orderItem.id,
    orderId: orderItem.order_id,
    reservationId: orderItem.reservation_id,
    status: orderItem.status,
    total: orderItem.total,
    cruiseOfferId: orderItem.cruiseOffer_id || orderItem.offer_id,
    transactionKey: orderItem.transactionKey,
    passengers: (orderItem.passengers ?? []).map(cruiseOrderItemTravellerMap),
    offerName: orderItem.offer_name,
  }
}

export function cruiseOrderItemTravellerMap(traveller: any) {
  return {
    firstName: traveller.first_name,
    lastName: traveller.last_name,
    type: traveller.type,
    isLeadPassenger: traveller.is_lead_passenger,
  }
}

const cruisesMapShip = (ship: CruisesContract.Ship, offerTitle: string): App.CruiseShip => ({
  id: ship.id,
  name: ship.name,
  images: ship.images ? ship.images.map(image => cruisesMapShipImages(image, offerTitle)) : [],
  shipInfo: ship.shipInfo?.map(cruisesMapShipInfo) ?? [],
  ...(ship.facts?.length && { facts: ship.facts }),
  ...(ship.decks?.length && { decks: ship.decks?.map(cruisesShipDeckMap) }),
  ...(ship.cabinCategories?.length && { cabinCategories: ship.cabinCategories?.map(cruisesMapCabinCategories) }),
  occupancies: {
    ...(!!ship.occupancies?.maxOccupancy && { maxOccupancy: ship.occupancies.maxOccupancy }),
    ...(!!ship.occupancies?.maxAdults && { maxAdults: ship.occupancies.maxAdults }),
    ...(!!ship.occupancies?.maxChildren && { maxChildren: ship.occupancies.maxChildren }),
    ...(!!ship.occupancies?.maxInfants && { maxInfants: ship.occupancies.maxInfants }),
    ...(!!ship.occupancies?.adultMinimumAge && { adultMinimumAge: ship.occupancies.adultMinimumAge }),
    ...(!!ship.occupancies?.childMaximumAge && { childMaximumAge: ship.occupancies.childMaximumAge }),
    ...(!!ship.occupancies?.childMinimumAge && { childMinimumAge: ship.occupancies.childMinimumAge }),
    ...(!!ship.occupancies?.infantMaximumAge && { infantMaximumAge: ship.occupancies.infantMaximumAge }),
    ...(!!ship.occupancies?.infantMinimumAge && { infantMinimumAge: ship.occupancies.infantMinimumAge }),
  },
  amenities: cruisesMapShipAmenities(ship.amenities ?? []),
})

const cruisesMapShipAmenities = (
  amenities?: Array<CruisesContract.ShipAmenity>,
): Array<App.CruiseShipAmenity> => {
  return amenities?.map((amenity) => ({
    id: amenity.id,
    title: amenity.title,
    description: amenity.description,
    category: amenity.category,
    imageId: amenity.imageId,
  })) ?? []
}

const cruisesMapCabinCategories = (cabinCategory: CruisesContract.ShipCabinCategory): App.CruiseShipCabinCategory => ({
  id: cabinCategory.id,
  cabinType: cabinCategory.cabinType,
  code: cabinCategory.code,
  ...(cabinCategory.color && { color: cabinCategory.color }),
  ...(cabinCategory.imageId && { imageId: cabinCategory.imageId }),
  ...(cabinCategory.cabinCategoryInfo?.length && {
    cabinCategoryInfo: cabinCategory.cabinCategoryInfo?.map(cruisesMapCabinInfo),
  }),
})

export const cruisesShipDeckMap = (deck: CruisesContract.ShipDeck): App.CruiseShipDeck => ({
  id: deck.id,
  name: deck.name,
  deckNumber: deck.deckNumber,
  externalId: deck.externalId,
  ...(deck.imageId && { imageId: deck.imageId }),
  ...(deck.shipCabinPosition && { shipCabinPosition: deck.shipCabinPosition }),
})

const cruisesMapCabinInfo = (cabinInfo: CruisesContract.ShipCabinCategoryInfo): App.CruiseShipCabinCategoryInfo => ({
  id: cabinInfo.id,
  name: cabinInfo.name,
  description: cabinInfo.description ?? '',
  languageCode: cabinInfo.languageCode,
})

const cruisesMapShipInfo = (shipInfo: CruisesContract.ShipInfo): App.CruiseShipInfo => {
  const shipMappedData = {
    title: shipInfo.title,
    description: shipInfo.description,
    dataTypeName: shipInfo.dataTypeName,
    amenityInfo: shipInfo.amenityInfo,
  }

  if (shipInfo.dataTypeName === 'General') {
    return {
      ...shipMappedData,
      description: shipInfo.description.replaceAll(/<\/?br\/?>/g, ''),
    }
  }

  return shipMappedData
}

const cruisesMapShipImages = (image: CruisesContract.ShipImage, offerTitle: string): App.CruiseShipImage => ({
  id: (image.idImage || image.imageId) as string,
  order: image.order,
  title: offerTitle,
})

const cruisesMapOfferImages = (image: CruisesContract.OfferImage): App.CruiseShipImage => ({
  id: image.imageId,
  order: image.order,
  title: image.description ?? '',
})

const cruiseFaqMap = (faq: any): App.CruiseFAQ => ({ // CruisesContract.Faq
  title: faq.title,
  content: faq.content,
  key: faq.key,
})

const cruiseFinePrintMap = (finePrint?: any): App.CruiseFinePrint => ({ // CruisesContract.FinePrint
  finalParagraph: finePrint?.finalParagraph,
  sections: finePrint?.sections?.map(cruiseFinePrintSectionMap) ?? [],
})

const cruiseFinePrintSectionMap = (finePrintSection: any): App.CruiseFinePrintSection => ({ // CruisesContract.FinePrintSection
  description: finePrintSection.introductoryText,
  items: finePrintSection.items ?? [],
  subSections: finePrintSection.subSections?.map(cruiseFinePrintSubSectionMap) ?? [],
  title: finePrintSection.topic,
})

const cruiseFinePrintSubSectionMap = (finePrintSubSection: any): App.CruiseFinePrintSection => ({ // CruisesContract.FinePrintSection
  items: finePrintSubSection.subSectionItems ?? [],
  title: finePrintSubSection.title,
})

const cruiseItinerary = (
  itinerary?: Array<CruisesContract.Itinerary>,
  cruiseTourItineraries?: Array<CruisesContract.Itinerary>,
): Array<App.CruiseItineraryItem> => {
  if (!itinerary) return []

  let itineraryItems: Array<App.CruiseItineraryItem> = []

  // Build cruise-tour itinerary
  if (cruiseTourItineraries) {
    itineraryItems = cruiseTourItineraries
      .map((cruiseTourItinerary) => {
        const itineraryItem = itinerary.find((itineraryItem) => itineraryItem.dayNumber === cruiseTourItinerary.dayNumber)
        return cruiseTourItineraryItemMap(cruiseTourItinerary, itineraryItem)
      })
  } else {
    // Build cruise-only itinerary
    itineraryItems = itinerary
      .map((itineraryItem) => cruiseItineraryItemMap(itineraryItem))
  }

  return itineraryItems
    .sort((a, b) => a.startDay - b.startDay)
}

const cruiseItineraryItemMap = (
  itineraryItem: CruisesContract.Itinerary,
): App.CruiseItineraryItem => {
  const mappedPort = cruiseItineraryItemPortMap(itineraryItem?.port)

  const originalTitle = itineraryItem.description && itineraryItem.description.toLowerCase() !== 'at sea' ?
      itineraryItem.description.trim() :
    null

  return {
    startDay: itineraryItem.dayNumber,
    title: originalTitle || (mappedPort?.portInfo?.map(item => item.title).join(', ') ?? ''),
    description: mappedPort?.portInfo?.map(item => item.description).join() ?? '',
    startTime: itineraryItem.arrivalTime,
    endTime: itineraryItem.departureTime,
    port: mappedPort,
  }
}

const cruiseTourItineraryItemMap = (
  cruiseTourItinerary: CruisesContract.Itinerary,
  itineraryItem?: CruisesContract.Itinerary,
): App.CruiseItineraryItem => {
  const mappedItineraryItem = itineraryItem ? cruiseItineraryItemMap(itineraryItem) : null

  if (mappedItineraryItem) {
    return {
      ...mappedItineraryItem,
      title: cruiseTourItinerary.description || mappedItineraryItem.title,
    }
  }

  return {
    startDay: cruiseTourItinerary.dayNumber,
    title: cruiseTourItinerary.description,
    description: cruiseTourItinerary.description,
    startTime: cruiseTourItinerary.arrivalTime,
    endTime: cruiseTourItinerary.departureTime,
    port: null,
  }
}

const cruiseItineraryItemPortMap = (port?: CruisesContract.FullPort): App.CruisePort | null => {
  if (!port) return null

  return {
    name: port.name,
    code: port.code,
    nearestAirportCode: port.nearestAirportCode,
    address: port.address,
    city: port.city,
    stateProvince: port.stateProvince,
    countryCode: port.countryCode,
    latitude: port.latitude,
    longitude: port.longitude,
    portInfo: port.portInfo?.map(cruiseItineraryItemPortInfoMap) ?? [],
  }
}

const cruiseItineraryItemPortInfoMap = (portInfo: CruisesContract.PortInfo): App.CruiseItineraryItemPortInfo => ({
  title: portInfo.title,
  description: portInfo.description ?? '',
  dataTypeName: portInfo.dataTypeName,
})

// BOOKING FLOW MAPPERS

export const cabinViewMap = (
  cruiseOffer: App.CruiseOffer,
): Array<App.CruiseCabinView> => {
  const { cabinCategories } = cruiseOffer.ship
  const categories: Array<App.CruiseShipCabinCategory> = Object.assign(cabinCategories || [])
  const cabinView: Array<App.CruiseCabinView> = categories.map((category, i) => {
    const categoryInfo = category.cabinCategoryInfo?.[0] ?? null

    return {
      id: category.id,
      cabin: {
        code: category.code,
        color: category.color ?? '',
        imageId: category.imageId ?? '',
        typeName: category.cabinType,
        categoryInfo: {
          id: categoryInfo?.id ?? i,
          name: categoryInfo?.name ?? category.cabinType,
          description: categoryInfo?.description ?? '',
          languageCode: categoryInfo?.languageCode ?? '',
        },
      },
    }
  })

  return cabinView
}

export const cruiseCabinRateDetailsMap = (
  details: string,
): App.CruiseCabinRateDetailsResponse => {
  const list = details?.match(/<li>.*?<\/li>/g) ?? [] as Array<string>

  const tempElement = document.createElement('div')
  tempElement.innerHTML = details
  const paragraphs = tempElement.getElementsByTagName('p')
  let termsAndConditions = ''
  for (let i = 0; i < paragraphs.length; i++) {
    const paragraph = paragraphs[i]
    if (paragraph.textContent) {
      termsAndConditions += paragraph.textContent.trim() + ' '
    }
  }
  termsAndConditions = `<ul>${termsAndConditions
    .split('. ')
    .filter(Boolean)
    .map((item) => `<li>${item}.</li>`)
    .join('')}</ul>`

  const filteredList = list.filter(
    item => !item.toLocaleLowerCase().includes('terms') || !item.toLocaleLowerCase().includes('conditions'),
  )

  const preview = `<ul>${(filteredList.slice(0, 6).join(''))}</ul>`
  const fullList = `<ul>${(filteredList.join(''))}</ul>`

  return {
    rawRateDetails: details,
    rateDetails: fullList,
    detailsPreview: preview,
    hasMoreDetails: list.length > 6,
    termsAndConditions,
  }
}

export const cruiseDepartureByIdMap = (
  departure: CruiseAPI.DepartureByIdResponse,
): App.CruiseDepartureView => ({
  id: departure.id,
  duration: departure.duration,
  returnDate: departure.returnDate,
  departureDate: departure.departureDate,
  offer: {
    id: departure.offer.id,
    name: departure.offer.displayName ?? departure.offer.name,
    samePorts: departure.offer.samePorts,
    returnPortName: departure.offer.returnPortName,
    departurePortName: departure.offer.departurePortName,
    numberOfCities: departure.offer.numberOfCities,
    numberOfCountries: departure.offer.numberOfCountries,
  },
  vendor: {
    id: departure.vendor.id,
    name: departure.vendor.name,
    imageId: departure.vendor.imageId ?? '',
  },
  ship: {
    id: departure.ship.id,
    name: departure.ship.name,
    imageId: departure.ship.imageId ?? '',
  },
})

export function cruiseBookingRateListMap(
  rateList: CruiseAPI.CruiseRateListResponse,
  offer: App.CruiseOffer,
  departureId: string,
  regionCode: string,
): App.Cruises.BookingRateListResponse {
  const pastPassengerRateList = rateList as CruiseAPI.CruiseRateListResponse & {
    isPastPassengerNumberAvailable?: boolean
    isPastPassengerNumberLookupAvailable?: boolean
    pastPassengerLookupFieldsRequired?: Array<App.Cruises.PastPassengerLookupField>
  }

  return {
    sessionId: rateList.sessionId,
    bookingId: rateList.bookingId,
    cabinHoldTimeout: rateList.cabinHoldTimeout,
    isPastPassengerNumberAvailable: pastPassengerRateList.isPastPassengerNumberAvailable,
    isPastPassengerNumberLookupAvailable: pastPassengerRateList.isPastPassengerNumberLookupAvailable,
    pastPassengerLookupFieldsRequired: pastPassengerRateList.pastPassengerLookupFieldsRequired,
    cabinRates: rateList.cabinRates.map((cabinRate) => cruiseBookingCabinRateMap(cabinRate, offer, departureId, regionCode)),
    cabinRatePackages: rateList.cabinRatePackages.map((cabinRate) => cruiseBookingCabinRatePackageMap(cabinRate, offer, departureId, regionCode)),
    personTitles: rateList.personTitles.map((title) => ({
      ...title,
      applicableGender: title.applicableGender as App.Cruises.ApplicableGender,
    })),
  }
}

export function cruiseVendorConfigurationMap(
  vendorConfiguration: CruiseAPI.CruiseVendorConfigurationResponse,
): App.Cruises.CruiseVendorConfiguration {
  return {
    ...vendorConfiguration,
    personTitlesSupported: vendorConfiguration.personTitlesSupported.map((title) => ({
      ...title,
      applicableGender: title.applicableGender,
    })),
  }
}

export function cruiseBookingCabinRatePackageMap(
  cabinRate: CruiseAPI.CruiseCabinRatePackage,
  offer: App.CruiseOffer,
  departureId: string,
  regionCode: string,
): App.Cruises.BookingCabinRatePackage {
  const { luxPlusInclusionsByTier, standardInclusions } = cruiseInclusionsMap({
    inclusions: offer.offerInclusions ?? [],
    departureId,
    lowestRateCode: cabinRate.rateCode,
    regionCode,
  })
  const excludedRates = cabinRate.excludedRates
    ?.filter((excludedRate) => excludedRate.rateCode !== cabinRate.rateCode)
    .map((excludedRate) => ({
      rateCode: excludedRate.rateCode,
      rateCodeDescription: excludedRate.rateCodeDescription ?? '',
      priceDetails: {
        price: excludedRate.priceDetails.price,
        memberPrice: excludedRate.priceDetails.memberPrice || 0,
        hasMemberPrice: excludedRate.priceDetails.hasMemberPrice,
        ...(!!excludedRate.priceDetails.discountDetails && {
          discountDetails: {
            amount: excludedRate.priceDetails.discountDetails.amount,
            discountPrice: excludedRate.priceDetails.discountDetails.discountPrice,
            discountPercentage: excludedRate.priceDetails.discountDetails.discountPercentage,
          },
        }),
        ...(!!excludedRate.priceDetails.onboardCreditDetails && {
          onboardCreditDetails: {
            amount: excludedRate.priceDetails.onboardCreditDetails.amount,
            currencyCode: excludedRate.priceDetails.onboardCreditDetails.currencyCode,
          },
        }),
      },
    }))

  return {
    name: cabinRate.name,
    description: cabinRate.description,
    termsAndConditions: cabinRate.termsAndConditions,
    isGrouped: cabinRate.isGrouped,
    rateCode: cabinRate.rateCode,
    cabinType: capitalise(cabinRate.cabinType),
    componentId: cabinRate.componentId,
    categoryName: cabinRate.categoryName,
    pricedCategoryCode: cabinRate.pricedCategoryCode,
    rateCodeDescription: cabinRate.rateCodeDescription ?? '',
    isNonRefundable: cabinRate.isNonRefundable,
    priceDetails: {
      price: cabinRate.priceDetails.price,
      memberPrice: cabinRate.priceDetails.memberPrice || 0,
      hasMemberPrice: cabinRate.priceDetails.hasMemberPrice,
      currencyCode: cabinRate.priceDetails.currencyCode,
      isMemberPricePublic: cabinRate.priceDetails.isMemberPricePublic ?? false,
      memberPriceSaving: cabinRate.priceDetails.memberPriceSaving,
      ...(!!cabinRate.priceDetails.discountDetails && {
        discountDetails: {
          amount: cabinRate.priceDetails.discountDetails.amount,
          discountPrice: cabinRate.priceDetails.discountDetails.discountPrice,
          discountPercentage: cabinRate.priceDetails.discountDetails.discountPercentage,
        },
      }),
      ...(!!cabinRate.priceDetails.onboardCreditDetails && {
        onboardCreditDetails: {
          amount: cabinRate.priceDetails.onboardCreditDetails.amount,
          currencyCode: cabinRate.priceDetails.onboardCreditDetails.currencyCode,
        },
      }),
    },
    ...(excludedRates && { excludedRates }),
    luxPlusInclusionsByTier,
    standardInclusions,
  }
}

export function cruiseBookingCabinRateMap(
  cabinRate: CruiseAPI.CruiseCabinRate,
  offer: App.CruiseOffer,
  departureId: string,
  regionCode: string,
): App.Cruises.BookingCabinRate {
  const { luxPlusInclusionsByTier, standardInclusions } = cruiseInclusionsMap({
    inclusions: offer.offerInclusions ?? [],
    departureId,
    lowestRateCode: cabinRate.rateCode,
    regionCode,
  })

  return {
    rateCode: cabinRate.rateCode,
    cabinType: capitalise(cabinRate.cabinType),
    componentId: cabinRate.componentId,
    categoryName: cabinRate.categoryName,
    pricedCategoryCode: cabinRate.pricedCategoryCode,
    rateCodeDescription: cabinRate.rateCodeDescription ?? '',
    isNonRefundable: cabinRate.isNonRefundable,
    priceDetails: {
      price: cabinRate.priceDetails.price,
      memberPrice: cabinRate.priceDetails.memberPrice || 0,
      hasMemberPrice: cabinRate.priceDetails.hasMemberPrice,
      ...(!!cabinRate.priceDetails.discountDetails && {
        discountDetails: {
          amount: cabinRate.priceDetails.discountDetails.amount,
          discountPrice: cabinRate.priceDetails.discountDetails.discountPrice,
          discountPercentage: cabinRate.priceDetails.discountDetails.discountPercentage,
        },
      }),
      ...(!!cabinRate.priceDetails.onboardCreditDetails && {
        onboardCreditDetails: {
          amount: cabinRate.priceDetails.onboardCreditDetails.amount,
          currencyCode: cabinRate.priceDetails.onboardCreditDetails.currencyCode,
        },
      }),
    },
    luxPlusInclusionsByTier,
    standardInclusions,
  }
}

export function cruiseBookingCabinListMap(cabinList: CruiseAPI.CruiseCabinListResponse): Array<App.Cruises.CruiseBookingCabin> {
  return cabinList.cabins.map<App.Cruises.CruiseBookingCabin>((cabin) => {
    return {
      categoryId: cabin.categoryId,
      cabinNumber: cabin.cabinNumber,
      minimumPassengers: cabin.minimumPassengers,
      maximumPassengers: cabin.maximumPassengers,
      isAccessibleCabin: cabin.isAccessibleCabin,
      locationDescription: cabin.locationDescription ?? '',
      isCabinInfoAvailable: cabin.isCabinInfoAvailable,
      deckId: cabin.deckId,
      bedType: cabin.bedType,
      deckName: cabin.deckName,
      connectingCabinNumber: cabin.connectingCabinNumber,
      componentIds: [],
    }
  })
}

export function cruiseBookingCabinSelectionMap(cabinSelection: CruiseAPI.CruiseCabinSelectionResponse): App.Cruises.CruiseBookingCabinSelection {
  const details = cabinSelection.details
  const isNonRefundable = details.pricing.isNonRefundable || false

  return {
    alternateDiningRequired: details.alternateDiningRequired,
    pricing: {
      ...details.pricing,
      cancellationPolicy: filterDataByHighestPercentage(details.pricing.cancellationSchedule || [], isNonRefundable),
    },
    tableSizes: details.tableSizes,
    diningOptions: details.diningOptions,
    bedConfigurations: details.bedConfigurations,
    prepaidGratuities: details.prepaidGratuities,
    insurance: details.insurance,
  }
}

export function cruiseBookingCabinPricingMap(cabinPricing: CruiseAPI.CruiseCabinPricingResponse): App.Cruises.CruiseBookingCabinPricing {
  const isNonRefundable = cabinPricing.details.pricing.isNonRefundable || false

  return {
    pricing: {
      ...cabinPricing.details.pricing,
      cancellationPolicy: filterDataByHighestPercentage(
        cabinPricing.details.pricing.cancellationSchedule || [],
        isNonRefundable,
      ),
    },
    paymentConfiguration: cabinPricing.details.paymentConfiguration,
  }
}

function cabinDetailsMap(cabinDetails: Array<string>) {
  // TODO: temporary implementation, we will move this to the backend
  const features = cabinDetails
    .filter((item) => item.includes(':') || item.toLowerCase().includes(' bed') || item.toLowerCase().includes(' beds'))
    .reduce((acc, item) => {
      let [key, value] = item.split(':')
      const isBedData = item.toLowerCase().includes('beds') && !item.includes(':')
      let formattedKey = isBedData ? 'bed_size' : key.trim().toLowerCase().replace(/\s/g, '_')

      if (formattedKey === 'cabin_dimensions') {
        value = value.replace('X', '-')
          .replace('FEET', 'ft')
          .replace('FT ft', 'ft')
          .replaceAll('"', '')
      }

      if (formattedKey === 'position_in_ship') {
        formattedKey = 'cabin_location'
      }

      if (formattedKey === 'cabin_location') {
        value = value.trim().toLowerCase()
      }

      if (formattedKey === 'bed_size') {
        if (item.toLowerCase().includes('unk')) {
          return acc
        }

        value = item.toLowerCase()
      }

      return {
        ...acc,
        [formattedKey]: value,
      }
    }, {})

  const details = cabinDetails.filter((item) => {
    const value = item.toLowerCase()

    return !value
      .match(/:|capacity|bed|inside|suite|balcony|oceanview/g)
  })

  return {
    features,
    details,
  }
}

export function cruiseBookingCabinDetailsListMap(
  cabinDetailsList: CruiseAPI.CruiseCabinDetailsListResponse,
): App.Cruises.BookingCabinDetailsList {
  return arrayToObject(
    cabinDetailsList.cabins,
    cabin => cabin.cabinNumber,
    cabin => cabinDetailsMap(cabin.cabinDetails || []),
  )
}

export const cruiseBookingRateDetailsListMap = (
  // TODO: use type when it's available
  rateDetailsList: any,
  // rateDetailsList: CruiseAPI.CruiseRateDetailsListResponse,
): Record<string, App.Cruises.BookingRateDetails> => {
  return rateDetailsList.rates.reduce((acc: Record<string, App.Cruises.BookingRateDetails>, rate: any) => {
    const details = rate.rateDetails?.[0] || ''
    const list: Array<string> = details?.match(/<li>.*?<\/li>/g) ?? []

    const tempElement = document.createElement('div')
    tempElement.innerHTML = details
    const paragraphs = tempElement.getElementsByTagName('p')
    let termsAndConditions = ''
    for (let i = 0; i < paragraphs.length; i++) {
      const paragraph = paragraphs[i]
      if (paragraph.textContent) {
        termsAndConditions += paragraph.textContent.trim() + ' '
      }
    }
    termsAndConditions = `<ul>${termsAndConditions
      .split('. ')
      .filter(Boolean)
      .map((item) => `<li>${item}.</li>`)
      .join('')}</ul>`

    const filteredList = list.filter((item) => {
      if (item.toLocaleLowerCase().includes('terms') || item.toLocaleLowerCase().includes('conditions')) {
        return false
      }
      return true
    })

    const preview = `<ul>${(filteredList.slice(0, 6).join(''))}</ul>`
    const fullList = `<ul>${(filteredList.join(''))}</ul>`

    acc[rate.componentId] = {
      rawRateDetails: details,
      rateDetails: fullList,
      detailsPreview: preview,
      hasMoreDetails: list.length > 6,
      termsAndConditions,
    }

    return acc
  }, {})
}

export const cruiseShipMap = (ship: CruiseAPI.CruiseShipResponse): App.Cruises.Ship => {
  return {
    id: ship.id,
    name: ship.name,
    captions: ship.captions,
    cabinCategoriesGroups: ship.cabinCategoriesGroups?.map((group) => ({
      id: group.id,
      name: group.name,
      description: group.description,
      cabinCategories: group.cabinCategories ?? [],
      images: group.images,
    })) ?? [],
    decks: ship.decks.map(deck => ({
      id: deck.id,
      name: deck.name,
      deckNumber: deck.deckNumber,
      ...(deck.imageId && { imageId: deck.imageId }),
      ...(deck.cabinPositions && { shipCabinPosition: deck.cabinPositions }),
    })),
  }
}

export const lowestPriceByCabinTypeMap = (
  cabinRates: Array<App.Cruises.BookingCabinRate>,
): Array<App.CruiseLowestPriceCabin> => {
  const cabinKey: { [k: string]: App.CruiseLowestPriceCabin } = {}

  const cabins = cabinRates.reduce((
    acc: Array<App.CruiseLowestPriceCabin>,
    cur: App.Cruises.BookingCabinRate) => {
    const hasItem = acc.find(item => item.typeName === cur.cabinType)

    hasItem ?
      cabinKey[cur.cabinType] = {
        typeName: cur.cabinType,
        lowestPrice: Math.min(hasItem.lowestPrice, cur.priceDetails.price),
      } :
      cabinKey[cur.cabinType] = {
        typeName: cur.cabinType,
        lowestPrice: cur.priceDetails.price,
      }

    return Object.values(cabinKey).map(item => item)
  }, []).sort((a, b) => a.lowestPrice - b.lowestPrice)

  return cabins
}

export function mapCruiseOfferMemberPricing(
  result: CruiseAPI.MemberPriceResponse,
): App.Cruises.CruiseOfferMemberPricing {
  return {
    isEligible: result.isEligible,
    departures: (result.departures ?? []).map((dep) => ({
      id: dep.id,
      lowestPricesByCategory: Object.fromEntries(
        Object.entries(dep.lowestPricesByCategory).map(([categoryKey, p]) => [
          categoryKey,
          {
            currencyCode: p.currencyCode,
            cabinCategory: p.cabinCategory,
            memberPrice: p.total,
          },
        ]),
      ),
    })),
  }
}

export const cruiseOfferSnapshotMap = (
  offer: CruiseAPI.CruiseOfferSnapshot,
): App.Cruises.CruiseOfferSnapshot => {
  return {
    ...offer,
    returnDate: convertTZ(new Date(offer.returnDate), 'UTC').toISOString(),
    departureDate: convertTZ(new Date(offer.departureDate), 'UTC').toISOString(),
    leVisibleDescription: offer.leVisibleDescription!,
    heroImageId: offer.heroImageId!,
    vendorImageId: offer.vendorImageId!,
    shipImageId: offer.shipImageId!,
    destinationDescription: offer.destinationDescription!,
    cabinImageId: offer.cabinImageId!,
    isSamePort: offer?.departurePortCode === offer?.returnPortCode,
    numberOfCountries: getNumberOfCountries(offer?.itineraries),
    numberOfCities: getNumberOfCities(offer?.itineraries),
    itineraries: offer.itineraries.map((itinerary) => {
      const port = itinerary.port ? {
        name: itinerary.port.name,
        city: itinerary.port.city!,
        countryCode: itinerary.port.countryCode!,
        latitude: itinerary.port.latitude!,
        longitude: itinerary.port.longitude!,
      } : undefined
      return {
        ...itinerary,
        port,
      }
    }),
  }
}

export const cruiseBookingMap = (
  booking: OrderTypes.CruiseDetailsResponse['result'],
  regionCode: string,
): App.Cruises.CruiseBooking => {
  const { standardInclusions, luxPlusInclusions } = cruiseBookingInclusionsMap(booking.inclusions ?? [], regionCode)

  return {
    ...booking,
    orderId: booking.orderId!,
    cabinCode: booking.cabinCode!,
    totalAmount: booking.totalAmount!,
    categoryId: booking.categoryId!,
    cabinNumber: booking.cabinNumber!,
    cabinType: booking.cabinType!,
    departureDate: booking.departureDate!,
    arrivalDate: booking.arrivalDate!,
    confirmationNumber: booking.confirmationNumber!,
    dining: booking.dining!,
    alternateDining: booking.alternateDining!,
    tableSize: booking.tableSize!,
    bedConfiguration: booking.bedConfiguration!,
    prepaidGratuities: booking.prepaidGratuities!,
    insurance: booking.insurance!,
    onboardCredit: booking.onboardCredit!,
    customerId: booking.customerId!,
    vendorCode: booking.vendorCode!,
    rateCodeDescription: booking.rateCodeDescription!,
    baseCurrency: booking.baseCurrency!,
    convertedCurrency: booking.convertedCurrency!,
    baseFxRate: booking.baseFxRate!,
    requiredFxRate: booking.requiredFxRate!,
    isNccfIncluded: booking.isNccfIncluded!,
    originalNccfAmount: booking.originalNccfAmount!,
    nccfAmount: booking.nccfAmount!,
    isTaxIncluded: booking.isTaxIncluded!,
    originalTaxAmount: booking.originalTaxAmount!,
    taxAmount: booking.taxAmount!,
    originalCommissionAmount: booking.originalCommissionAmount!,
    commissionAmount: booking.commissionAmount!,
    originalTotalAmount: booking.originalTotalAmount!,
    originalNetFareAmount: booking.originalNetFareAmount!,
    netFareAmount: booking.netFareAmount!,
    failureReason: booking.failureReason!,
    vendorBookingStatus: booking.vendorBookingStatus!,
    isNonRefundable: booking.isNonRefundable ?? undefined,
    travellersDetails: booking.travellersDetails.map((traveller) => {
      return {
        ...traveller,
        dateOfBirth: traveller.dateOfBirth!,
        middleName: traveller.middleName!,
      }
    }),
    providerPaymentSchedules: booking.providerPaymentSchedules.map((schedule) => {
      return {
        ...schedule,
        providerDueDate: schedule.providerDueDate!,
      }
    }),
    rateCodeDetails: booking.rateCodeDetails ? cruiseCabinRateDetailsMap(booking.rateCodeDetails) : undefined,
    ...(!!booking.cancellationSchedules && {
      cancellationSchedule: booking.cancellationSchedules,
    }),
    standardInclusions,
    luxPlusInclusions,
    bundles: mapCruiseBookingBundles(booking.bundles),
  }
}

const getNumberOfCountries = (
  itineraries: Array<CruiseAPI.CruiseItinerarySnapshot>,
): number => {
  const countries = new Set()
  itineraries?.forEach((itinerary) => {
    if (itinerary.port?.countryCode) {
      countries.add(itinerary.port.countryCode)
    }
  })
  return countries.size
}

const getNumberOfCities = (
  itineraries: Array<CruiseAPI.CruiseItinerarySnapshot>,
): number => {
  const cities = new Set()
  itineraries?.forEach((itinerary) => {
    if (itinerary.port?.city) {
      cities.add(itinerary.port.city.toLocaleLowerCase())
    } else if (itinerary.port?.name) {
      cities.add(itinerary.port.name.split(',')[0].toLocaleLowerCase())
    } else if (itinerary.description !== 'At Sea') {
      cities.add(itinerary.description.split(',')[0].toLocaleLowerCase())
    }
  })
  return cities.size
}

// We also want to capitalise the first letter after a hyphen and forward slash
function capitaliseAll(str = ''): string {
  if (!str) return str

  const wordList = str.split(' ')
  if (wordList.length > 1) {
    const capitalisedWordList = wordList.map((word, index) => {
      if (isPartOfShipName(word)) {
        return word.toUpperCase()
      }

      if (index === 0 || !capitaliseIgnoreSet.has(word)) {
        return capitaliseWord(word)
      }

      return word
    })

    return capitalisedWordList.join(' ')
  }

  return capitaliseWord(str)
}

const isPartOfShipName = (word: string) => {
  return isRomanNumeral(word) || word === 'msc' || word === 'ms' || word === 's.s.'
}

const isRomanNumeral = (word: string) => {
  return word === 'i' || word === 'ii' || word === 'iii' || word === 'iv' || word === 'v' || word === 'vi' || word === 'vii' || word === 'viii' || word === 'ix' || word === 'x'
}

const isOpenParenthesis = (word: string) => {
  return word[0] === '('
}

// Split by spaces first, then for each word, split by hyphens and forward slashes and capitalise each part
const capitaliseWord = (word: string) => {
  return word
    .split(/[-/]/)
    .map((part, idx) => {
      // We need to deal with open parentheses at the beginning of the word. Parentheses are common in cruise names
      if (isOpenParenthesis(part)) {
        return '(' + part.charAt(1).toUpperCase() + part.slice(2)
      }

      // For the first word in the string, always capitalise
      // For subsequent words, use the ignore set logic
      if (idx === 0 || (part.length > 2 && !capitaliseIgnoreSet.has(part))) {
        return capitalise(part)
      }

      return part
    })
    .join(word.includes('/') ? '/' : '-')
}
